import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { validateEmail } from '@/utils/validators'
import { useForceLightMode } from '@/contexts/ThemeContext'

type LoginForm = {
    email: string
    password: string
}

type FormErrors = {
    email?: string
    password?: string
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate()
    const { user, isLoading, isAuthenticated, error, login, clearError } = useAuth()
    useForceLightMode()

    const [formData, setFormData] = useState<LoginForm>({
        email: '',
        password: ''
    })

    const [errors, setErrors] = useState<FormErrors>({})
    const [showPassword, setShowPassword] = useState(false)

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            navigate('/dashboard')
        }
    }, [isAuthenticated, user, navigate])

    // Clear error on unmount
    useEffect(() => {
        return () => {
            clearError()
        }
    }, [clearError])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target

        setFormData(prev => ({
            ...prev,
            [name]: value
        }))

        // Clear error for the field being edited
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }))
        }
    }

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {}

        if (!formData.email) {
            newErrors.email = 'Email is required'
        } else {
            const emailValidation = validateEmail(formData.email)
            if (!emailValidation.isValid) {
                newErrors.email = emailValidation.error
            }
        }

        if (!formData.password) {
            newErrors.password = 'Password is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        try {
            await login({
                email: formData.email,
                password: formData.password
            })
        } catch (error) {
            // Error is handled by the useAuth hook
            console.error('Login failed:', error)
        }
    }

    return (
        <div className="min-h-screen flex flex-col items-center pt-32 md:pt-40 bg-slate-50 dark:bg-slate-950 py-12 px-4 relative overflow-hidden" data-testid="login-page">
            {/* ── Minimalist Background ── */}
            <div className="absolute top-[-10%] right-[-5%] w-[30%] h-[30%] bg-primary/5 rounded-full blur-[100px]" />
            <div className="absolute bottom-[-5%] left-[-5%] w-[25%] h-[25%] bg-slate-200 dark:bg-primary/5 rounded-full blur-[80px]" />

            <div className="max-w-md w-full space-y-8 relative z-10">
                {/* ── Header ── */}
                <div className="text-center space-y-2">
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                        Selamat Datang Kembali
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">
                        Silakan masuk untuk mengakses kursus Anda
                    </p>
                </div>

                {/* ── Login Card ── */}
                <div className="bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl p-8 sm:p-10 rounded-3xl border border-white dark:border-white/5 shadow-2xl shadow-slate-200/50 dark:shadow-none">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {/* General error from auth context */}
                        {error && (
                            <div role="alert" className="bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 p-4 rounded-xl text-sm font-medium flex items-center gap-2">
                                <span className="material-symbols-outlined text-[20px]">error</span>
                                {error}
                            </div>
                        )}

                        {/* Email field */}
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                                Alamat Email
                            </label>
                            <div className="relative group">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">mail</span>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className="block w-full pl-12 pr-4 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                                    placeholder="Masukkan email"
                                    autoComplete="email"
                                    required
                                />
                            </div>
                        </div>

                        {/* Password field */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-center px-1">
                                <label htmlFor="password" className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    Kata Sandi
                                </label>
                            </div>
                            <div className="relative group">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 group-focus-within:text-primary transition-colors text-[20px]">lock</span>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="block w-full pl-12 pr-12 py-3 bg-slate-100/50 dark:bg-white/5 border border-slate-200 dark:border-transparent focus:border-primary/50 focus:bg-white dark:focus:bg-white/10 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                                    placeholder="Masukkan kata sandi"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <span className="material-symbols-outlined text-[20px]">{showPassword ? 'visibility_off' : 'visibility'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Submit button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-3.5 px-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-slate-200 dark:shadow-none disabled:opacity-50 mt-2"
                        >
                            {isLoading ? 'Masuk...' : 'Masuk ke Akun'}
                        </button>

                        {/* Register link */}
                        <div className="text-center pt-2">
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                Belum punya akun?{' '}
                                <Link to="/register" className="text-primary font-bold hover:underline">Daftar Sekarang</Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default LoginPage
